/**
 * Dates, money and the handful of names the Act uses.
 *
 * Dates are written out in words rather than in digits, because a date shown as
 * 05/06 is read two ways and one of the dates on this screen is the day an
 * applicant acquires a statutory right.
 */

import { bi, type Bilingual, type BoardType, type Lang } from "@ksidc/ontology";

const MONTHS_EN = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MONTHS_ML = [
  "ജനുവരി",
  "ഫെബ്രുവരി",
  "മാർച്ച്",
  "ഏപ്രിൽ",
  "മേയ്",
  "ജൂൺ",
  "ജൂലൈ",
  "ഓഗസ്റ്റ്",
  "സെപ്റ്റംബർ",
  "ഒക്ടോബർ",
  "നവംബർ",
  "ഡിസംബർ",
];

/** Accepts a YYYY-MM-DD calendar date or a full ISO instant. */
export function formatDate(value: string | null | undefined, lang: Lang): string {
  if (!value) return lang === "ml" ? "രേഖപ്പെടുത്തിയിട്ടില്ല" : "not recorded";
  const iso = value.length === 10 ? `${value}T00:00:00+05:30` : value;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return value;

  // Everything statutory is counted in Indian Standard Time.
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? "";
  const day = Number(get("day"));
  const monthIndex = Number(get("month")) - 1;
  const year = get("year");
  const month = (lang === "ml" ? MONTHS_ML : MONTHS_EN)[monthIndex] ?? "";
  return lang === "ml" ? `${year} ${month} ${day}` : `${day} ${month} ${year}`;
}

export function formatDateTime(value: string | null | undefined, lang: Lang): string {
  if (!value) return lang === "ml" ? "രേഖപ്പെടുത്തിയിട്ടില്ല" : "not recorded";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
  return `${formatDate(value, lang)}, ${time}`;
}

export const formatRupees = (n: number): string => `₹${Math.round(n).toLocaleString("en-IN")}`;

/** The same figure said the way people say it, so the number can be checked. */
export function inWords(rupees: number, lang: Lang): string {
  if (!Number.isFinite(rupees) || rupees <= 0) return "";
  if (rupees >= 1_00_00_000) {
    const crore = (rupees / 1_00_00_000).toFixed(2).replace(/\.00$/, "");
    return lang === "ml" ? `${crore} കോടി രൂപ` : `${crore} crore rupees`;
  }
  if (rupees >= 1_00_000) {
    const lakh = (rupees / 1_00_000).toFixed(2).replace(/\.00$/, "");
    return lang === "ml" ? `${lakh} ലക്ഷം രൂപ` : `${lakh} lakh rupees`;
  }
  return lang === "ml" ? `${rupees.toLocaleString("en-IN")} രൂപ` : `${rupees.toLocaleString("en-IN")} rupees`;
}

/** Days, said in words, so "1 day(s)" never reaches an applicant. */
export function days(n: number): Bilingual {
  const plain = Math.abs(n);
  return bi(plain === 1 ? "1 day" : `${plain} days`, plain === 1 ? "ഒരു ദിവസം" : `${plain} ദിവസം`);
}

export function workingDays(n: number, workingOnly: boolean): Bilingual {
  const plain = Math.abs(n);
  if (!workingOnly) return days(n);
  return bi(
    plain === 1 ? "1 working day" : `${plain} working days`,
    plain === 1 ? "ഒരു പ്രവൃത്തി ദിവസം" : `${plain} പ്രവൃത്തി ദിവസം`,
  );
}

export const BOARD_NAMES: Record<BoardType, Bilingual> = {
  STATE: bi(
    "State Single Window Clearance Board",
    "സംസ്ഥാന ഏകജാലക അനുമതി ബോർഡ്",
  ),
  // The kind of board, not a particular one. The board's own name comes from
  // the API and carries its district; this is the fallback when it has not
  // arrived yet, and naming a district here would be a guess.
  DISTRICT: bi(
    "District Single Window Clearance Board",
    "ജില്ലാ ഏകജാലക അനുമതി ബോർഡ്",
  ),
  INDUSTRIAL_AREA: bi(
    "Industrial Area Single Window Clearance Board",
    "വ്യവസായ മേഖലാ ഏകജാലക അനുമതി ബോർഡ്",
  ),
};

/** The test that decided the board, said as a sentence rather than a code. */
export const ROUTING_TEST_NAMES: Record<string, Bilingual> = {
  SITE_INSIDE_NOTIFIED_AREA: bi(
    "Decided by where the site is",
    "സ്ഥലം എവിടെയാണ് എന്നതിനെ ആശ്രയിച്ചാണ് ഇത് തീരുമാനിച്ചത്",
  ),
  OUTLAY_AT_OR_BELOW_CEILING: bi(
    "This Board decides it because the project costs fifteen crore rupees or less",
    "പദ്ധതിയുടെ വലുപ്പം അനുസരിച്ചാണ് ഇത് തീരുമാനിച്ചത്; അത് പതിനഞ്ച് കോടി രൂപ പരിധിക്കുള്ളിലാണ്",
  ),
  OUTLAY_ABOVE_CEILING: bi(
    "This Board decides it because the project costs more than fifteen crore rupees",
    "പദ്ധതിയുടെ വലുപ്പം അനുസരിച്ചാണ് ഇത് തീരുമാനിച്ചത്; അത് പതിനഞ്ച് കോടി രൂപ പരിധിക്ക് മുകളിലാണ്",
  ),
};

/** How the period is counted, in words the applicant can check against the Act. */
export const COUNTING_RULE_NAMES: Record<string, Bilingual> = {
  WORKING_DAYS_FROM_RECEIPT: bi(
    "Thirty working days, counted from the day the Board received your application. Sundays, the second Saturday of each month and declared holidays are not counted. Other Saturdays are working days.",
    "മുപ്പത് പ്രവൃത്തി ദിവസം, ബോർഡിന് അപേക്ഷ ലഭിച്ച ദിവസം മുതൽ. ഞായറാഴ്ചകൾ, ഓരോ മാസത്തെയും രണ്ടാം ശനിയാഴ്ച, പ്രഖ്യാപിത അവധി ദിവസങ്ങൾ എന്നിവ കണക്കാക്കില്ല. മറ്റ് ശനിയാഴ്ചകൾ പ്രവൃത്തി ദിവസങ്ങളാണ്.",
  ),
  CALENDAR_DAYS_FROM_RECEIPT: bi(
    "Thirty days, counted from the day the Board received your application. Every day counts, including holidays.",
    "മുപ്പത് ദിവസം, ബോർഡിന് അപേക്ഷ ലഭിച്ച ദിവസം മുതൽ. അവധി ദിവസങ്ങൾ ഉൾപ്പെടെ എല്ലാ ദിവസവും കണക്കാക്കും.",
  ),
  CALENDAR_DAYS_FROM_SUBMISSION: bi(
    "Thirty days, counted from the day you submitted your application. Every day counts, including holidays.",
    "മുപ്പത് ദിവസം, നിങ്ങൾ അപേക്ഷ സമർപ്പിച്ച ദിവസം മുതൽ. അവധി ദിവസങ്ങൾ ഉൾപ്പെടെ എല്ലാ ദിവസവും കണക്കാക്കും.",
  ),
};

/** What an authority sent back, in plain words. */
export const RESPONSE_KIND_NAMES: Record<string, Bilingual> = {
  RECOMMEND: bi("Recommended", "ശുപാർശ ചെയ്തു"),
  RECOMMEND_WITH_CONDITIONS: bi("Recommended, with conditions", "വ്യവസ്ഥകളോടെ ശുപാർശ ചെയ്തു"),
  OBJECT: bi("Objected", "എതിർപ്പ് അറിയിച്ചു"),
  NO_OBJECTION: bi("No objection", "എതിർപ്പില്ല"),
  QUERY: bi("Asked a question", "ഒരു ചോദ്യം ഉന്നയിച്ചു"),
};

export const MESSAGE_KIND_NAMES: Record<string, Bilingual> = {
  query: bi("A question about your application", "നിങ്ങളുടെ അപേക്ഷ സംബന്ധിച്ച ചോദ്യം"),
  inspection_notice: bi("Notice of site inspection", "സ്ഥലപരിശോധനാ അറിയിപ്പ്"),
  decision: bi("The decision on your application", "നിങ്ങളുടെ അപേക്ഷയിലെ തീരുമാനം"),
  certificate: bi("A certificate has been issued", "ഒരു സർട്ടിഫിക്കറ്റ് നൽകിയിട്ടുണ്ട്"),
};

export const messageKindName = (kind: string): Bilingual =>
  MESSAGE_KIND_NAMES[kind] ?? bi("A message from the Board", "ബോർഡിൽ നിന്നുള്ള സന്ദേശം");
